/** 28.09 (владелец): «админ зала видит все данные об играх в этот день на этом филиале и
 *  расстановку операторов (кто, какие мастер-классы ведёт)»; «может перемещать операторов,
 *  добавлять, то же с МК — через одобрение ОО, история сохраняется»; «может вписать, как
 *  рассчитались игроки». GET /api/assignments/hall-day (+ /hall-day/extra для правок) —
 *  сервер пускает только админа этого филиала в этот день и только после «Отправить». */
import { useCallback, useEffect, useState } from "react";
import { api } from "../api/client";
import { fmtDateApi } from "../utils/date";

export interface HallOp { id: string; name: string }
export interface HallGame {
  booking_id: number; date: string; time: string; night: boolean; quest: string; special: boolean; need: number; duration: number;
  players: number; tea: boolean; tea_room: string | null; prepaid: boolean; prepaid_amount: number;
  comment: string; client: string; phone: string | null; birthday: boolean; competition: boolean; source: string;
  crew: (HallOp & { accepted: boolean; status: string })[];
  mcs: { id: string; name: string; ops: HallOp[] }[];
}
export interface HallDay {
  date: string; branch: { id: string; name: string }; office: boolean; hours: string;
  me: { accepted: boolean; sent: boolean } | null;
  admins: { id: string; name: string; accepted: boolean }[];
  games: HallGame[];
}
export type ReqKind = "add" | "remove" | "move" | "mc_add" | "mc_remove" | "mc_new";
export interface HallReq {
  id: number; kind: ReqKind; booking_id: number; from_booking_id: number | null; operator_id: string | null; mc_id: string | null;
  summary: string; note: string | null; requested_by_name: string; requested_at: string;
  status: "pending" | "approved" | "rejected" | "failed" | "cancelled"; decided_by_name: string | null; decided_at: string | null;
  decision_note: string | null; apply_result: string | null;
}
export interface PoolOp extends HallOp { avail: string[]; busy: string[]; /** 16.10: «не показывать в расстановке» (HR) */ board_hidden?: boolean }
export interface HallPay { cash: number; terminal: number; transfer: number; invoice: number; other: number; change_amount: number; change_source: string; entered_by_name: string; updated_at: string }
export interface HallExtra { pool: PoolOp[]; mc_catalog: { id: string; name: string; duration?: number }[]; requests: HallReq[]; payments: Record<string, HallPay> }
export type ReqIn = { kind: ReqKind; booking_id: number; from_booking_id?: number; operator_id?: string; mc_id?: string; note?: string };

export function useHallDay(date: Date, branch: string | null) {
  const [day, setDay] = useState<HallDay | null>(null);
  const [extra, setExtra] = useState<HallExtra | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);
  const d = fmtDateApi(date);
  useEffect(() => {
    if (!branch) { setDay(null); setExtra(null); return; }
    const ac = new AbortController();
    const q = `date=${d}&branch=${encodeURIComponent(branch)}`;
    setError(null);
    api<HallDay>(`/api/assignments/hall-day?${q}`, { signal: ac.signal })
      .then((r) => { if (!ac.signal.aborted) setDay(r); })
      .catch((e: unknown) => { if (!ac.signal.aborted) { setDay(null); setError(e instanceof Error ? e.message : "Не загрузилось"); } });
    api<HallExtra>(`/api/assignments/hall-day/extra?${q}`, { signal: ac.signal })
      .then((r) => { if (!ac.signal.aborted) setExtra(r); }).catch(() => { if (!ac.signal.aborted) setExtra(null); });
    return () => ac.abort();
  }, [d, branch, tick]);
  /* другой день — прежние игры и телефоны не показываем, пока грузится */
  useEffect(() => { setDay(null); setExtra(null); }, [d, branch]);
  const accept = useCallback(async () => {
    if (!branch) return;
    await api("/api/assignments/hall-day/accept", { method: "POST", body: { date: d, branch } }).catch(() => {});
    reload();
  }, [d, branch, reload]);
  /** Заявка в ОО. Возвращает текст ошибки (или null). */
  const request = useCallback(async (r: ReqIn): Promise<string | null> => {
    if (!branch) return "нет филиала";
    try {
      const res = await api<{ ok: boolean; error?: string; status?: string }>("/api/assignments/hall-day/requests", { method: "POST", body: { date: d, branch, ...r } });
      reload();
      return res.ok ? (res.status === "failed" ? res.error ?? "не применилось" : null) : res.error ?? "не принято";
    } catch (e: unknown) { reload(); return e instanceof Error ? e.message : "не отправилось"; }
  }, [d, branch, reload]);
  const cancel = useCallback(async (id: number) => {
    await api(`/api/assignments/hall-day/requests/${id}`, { method: "DELETE" }).catch(() => {});
    reload();
  }, [reload]);
  const pay = useCallback(async (bookingId: number, body: Record<string, unknown>): Promise<string | null> => {
    try { await api("/api/assignments/hall-day/payment", { method: "POST", body: { booking_id: bookingId, ...body } }); reload(); return null; }
    catch (e: unknown) { return e instanceof Error ? e.message : "не сохранилось"; }
  }, [reload]);
  return { day, extra, error, reload, accept, request, cancel, pay };
}

/** «Кто сегодня на филиале»: человек → игры, МК. */
export function hallRoster(games: HallGame[]): { name: string; games: number; mcs: string[] }[] {
  const by = new Map<string, { name: string; games: number; mcs: string[] }>();
  for (const g of games) {
    for (const o of g.crew) { const r = by.get(o.id) ?? { name: o.name, games: 0, mcs: [] }; r.games++; by.set(o.id, r); }
    for (const m of g.mcs) for (const o of m.ops) {
      const r = by.get(o.id) ?? { name: o.name, games: 0, mcs: [] };
      if (!r.mcs.includes(m.name)) r.mcs.push(m.name);
      by.set(o.id, r);
    }
  }
  return [...by.values()];
}

/** Свободен ли по графику всё время игры (как квадратик на доске ОО, canWholeGame). */
export function freeForGame(op: PoolOp, time: string, minutes: number): boolean {
  const [h, m] = time.split(":").map(Number);
  const start = (h ?? 0) * 60 + (m ?? 0);
  for (let t = Math.floor(start / 60); t * 60 < start + (minutes || 60); t++) {
    if (!op.avail.includes(`${String(((t % 24) + 24) % 24).padStart(2, "0")}:00`)) return false;
  }
  return true;
}
